import { DrawingControls } from "./DrawingControls";
import { duplicateSelection } from "./sheet-operations";
import { CompositionControls } from "./CompositionControls";
import { Copy, Trash2, Lock, X, Sparkles } from "lucide-react";
import { isSketchSelection } from "../shared/sketch-generation";
import { useEffect, useRef } from "react";
import type { WorkspaceModel } from "./useWorkspace";

export function SelectionStyle({
  model,
  onClose,
  onSketch,
}: {
  model: WorkspaceModel;
  onClose?: () => void;
  onSketch?: () => void;
}) {
  const { editor, selected, tool } = model;
  const heading = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    const active = document.activeElement;
    // Only an explicit Properties action moves focus. Selection changes can
    // remount this panel without taking focus from the user's current control.
    if (
      active instanceof HTMLButtonElement &&
      active.isConnected &&
      (/properties/i.test(active.getAttribute("aria-label") || active.title) ||
        active.title === "Table options")
    ) {
      opener.current = active;
      heading.current?.focus();
    }
  }, []);
  const close = () => {
    onClose?.();
    if (opener.current?.isConnected) opener.current.focus();
    else editor.focus();
  };
  const single =
    tool === "select" && selected.length === 1 ? selected[0] : undefined;
  const asset =
    single?.type === "image" && single.props.assetId
      ? editor.getAsset(single.props.assetId)
      : undefined;
  const label = single
    ? (
        {
          sheet: "Sheet",
          image: asset?.meta.pdfPages
            ? "PDF"
            : asset?.meta.documentSource && !asset.meta.imageFile
              ? "Document preview"
              : "Image",
          text: "Text",
          trace: "Tracing paper",
          document: "Document",
          table: "Table",
          mark: "Measurement",
          geo: "Shape",
          draw: "Stroke",
          arrow: "Arrow",
          group: "Group",
          note: "Sticky note",
        } as Record<string, string>
      )[single.type] || "Selection"
    : tool === "select" && selected.length
      ? `${selected.length} selected`
      : "Drawing";
  if (tool === "eraser") return null;
  return (
    <div
      className="selection-style floating-surface"
      role="region"
      aria-label={`${label} properties`}
      onKeyDown={(event) => {
        // Events from a portaled child menu bubble in React, but its Escape
        // should close that menu alone.
        if (
          event.key !== "Escape" ||
          event.defaultPrevented ||
          !event.currentTarget.contains(event.target as Node)
        )
          return;
        event.preventDefault();
        event.stopPropagation();
        close();
      }}
    >
      <div ref={heading} className="properties-heading" tabIndex={-1}>
        <strong>{label} properties</strong>
        {onClose && (
          <button aria-label="Close properties" onClick={close}>
            <X size={16} />
          </button>
        )}
      </div>
      <div className="properties-body">
        <DrawingControls model={model} />
        {single && (single.type === "geo" || single.type === "text") && (
          <label>
            Exact colour{" "}
            <input
              type="color"
              aria-label="Exact colour"
              value={
                /^#[a-fA-F0-9]{6}$/.test(String(single.meta.exactColor))
                  ? String(single.meta.exactColor)
                  : "#1d1d1d"
              }
              disabled={
                editor.getIsReadonly() || editor.isShapeOrAncestorLocked(single)
              }
              onChange={(event) =>
                editor.updateShape({
                  id: single.id,
                  type: single.type,
                  meta: {
                    ...single.meta,
                    exactColor: event.target.value,
                    exactFillColor: null,
                    exactStrokeColor: null,
                  },
                })
              }
            />
            <span>
              {single.meta.exactColor
                ? String(single.meta.exactColor)
                : "Preset"}
            </span>
          </label>
        )}
        <CompositionControls model={model} />
      </div>
      {selected.length > 0 && tool === "select" && (
        <div className="property-utilities">
          <span className="property-section-title">Object actions</span>
          <div className="quick-actions">
            <button
              title="Duplicate selection"
              onClick={() => duplicateSelection(editor)}
            >
              <Copy size={15} />
              Duplicate
            </button>
            <button
              aria-label="Lock selection"
              title="Lock selection"
              onClick={() => {
                const ids = editor.getSelectedShapeIds();
                editor.markHistoryStoppingPoint("Lock");
                editor.toggleLock(ids);
                editor.select(...ids);
              }}
            >
              <Lock size={15} />
            </button>
            <button
              aria-label="Delete selection"
              title="Delete selection"
              onClick={() => {
                editor.markHistoryStoppingPoint("Delete");
                editor.deleteShapes(editor.getSelectedShapeIds());
              }}
            >
              <Trash2 size={15} />
            </button>
          </div>
          {onSketch && isSketchSelection(selected) && (
            <button className="sketch-secondary-action" onClick={onSketch}>
              <Sparkles size={15} />
              Create from sketch
            </button>
          )}
        </div>
      )}
    </div>
  );
}
